import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  TextField,
  MenuItem,
  Alert,
  Box,
  Chip,
  IconButton,
  Paper,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import styles from "../Dashboard.module.css";
import { useOperatorsApi } from "../../../hooks/useOperatorsApi";
import { formatDateTime } from "../../../utils/formatDateTime";

const paginationModel = { page: 0, pageSize: 10 };

const reportTypes = [
  { value: "outbound-messages-basic", label: "Outbound Messages - Basic" },
  {
    value: "outbound-messages-detailed",
    label: "Outbound Messages - Detailed",
  },
  { value: "inbound-messages", label: "Inbound Messages" },
  { value: "credits-purchased-basic", label: "Credits Purchased - Basic" },
  {
    value: "credits-purchased-detailed",
    label: "Credits Purchased - Detailed",
  },
  { value: "operator-pay-basic", label: "Operator Pay - Basic" },
  { value: "operator-pay-detailed", label: "Operator Pay - Detailed" },
  { value: "client-billing", label: "Client Message Billing" },
];

export default function Reports() {
  const { operators, fetchOperators, fetchOperatorsByClient } =
    useOperatorsApi();

  const [selectedReport, setSelectedReport] = useState(
    "outbound-messages-basic"
  );
  
  // Initialize dates in YYYY-MM-DD format (local timezone)
  const getDefaultDate = (daysOffset = 0) => {
    const date = new Date();
    date.setDate(date.getDate() + daysOffset);
    // Format as YYYY-MM-DD in local timezone
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Convert local date/time to UTC ISO string for API
  const localToUtcIso = (dateStr, timeStr, useEndOfMinute = false) => {
    // Parse date components
    const [year, month, day] = dateStr.split('-').map(Number);
    
    // Parse time components - handle both HH:mm and HH:mm:ss formats
    const timeParts = timeStr.split(':').map(Number);
    const hours = timeParts[0] || 0;
    const minutes = timeParts[1] || 0;
    // For end time, use 59 seconds to include the full minute
    const seconds = useEndOfMinute ? 59 : (timeParts[2] || 0);
    
    // Create date in local timezone (this constructor always uses local time)
    const localDate = new Date(year, month - 1, day, hours, minutes, seconds);
    
    // Return as ISO string (automatically converts to UTC)
    return localDate.toISOString();
  };
  
  const [startDate, setStartDate] = useState(getDefaultDate(-30));
  const [endDate, setEndDate] = useState(getDefaultDate(0));
  const [startTime, setStartTime] = useState("00:00");
  const [endTime, setEndTime] = useState("23:59");
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState("all");
  const [selectedOperator, setSelectedOperator] = useState("all");

  useEffect(() => {
    fetchClients();
    fetchOperators();
  }, []);

  useEffect(() => {
    if (selectedClient && selectedClient !== "all") {
      fetchOperatorsByClient(selectedClient);
    } else {
      setSelectedOperator("all");
      fetchOperators();
    }
  }, [selectedClient]);

  // Auto-generate report when any filter changes
  useEffect(() => {
    generateReport();
  }, [
    selectedReport,
    startDate,
    startTime,
    endDate,
    endTime,
    selectedClient,
    selectedOperator,
  ]);

  const fetchClients = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch("/api/clients/dropdown", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setClients(data || []);
      }
    } catch (error) {
      console.error("Error fetching clients:", error);
    }
  };

  const combineDateTime = (date, month, year, time) => {
    const dateStr = `${year}-${month.padStart(2, "0")}-${date.padStart(
      2,
      "0"
    )}`;
    return new Date(`${dateStr}T${time}:00.000Z`).toISOString();
  };

  const getDateRangeDisplay = () => {
    // Parse date and time components explicitly to avoid browser inconsistencies
    const [startYear, startMonth, startDay] = startDate.split('-').map(Number);
    const startTimeParts = startTime.split(':').map(Number);
    const startLocal = new Date(startYear, startMonth - 1, startDay, startTimeParts[0] || 0, startTimeParts[1] || 0, 0);
    
    const [endYear, endMonth, endDay] = endDate.split('-').map(Number);
    const endTimeParts = endTime.split(':').map(Number);
    const endLocal = new Date(endYear, endMonth - 1, endDay, endTimeParts[0] || 0, endTimeParts[1] || 0, 59);
    
    const formatLocal = (date) => {
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      const hour = String(date.getHours()).padStart(2, "0");
      const minute = String(date.getMinutes()).padStart(2, "0");
      const second = String(date.getSeconds()).padStart(2, "0");
      return `${day}/${month}/${year} ${hour}:${minute}:${second}`;
    };
    
    return `${formatLocal(startLocal)} - ${formatLocal(endLocal)}`;
  };

  const generateDays = () => {
    const days = [];
    for (let i = 1; i <= 31; i++) {
      days.push({ value: i.toString().padStart(2, "0"), label: i.toString() });
    }
    return days;
  };

  const generateMonths = () => {
    return [
      { value: "01", label: "January" },
      { value: "02", label: "February" },
      { value: "03", label: "March" },
      { value: "04", label: "April" },
      { value: "05", label: "May" },
      { value: "06", label: "June" },
      { value: "07", label: "July" },
      { value: "08", label: "August" },
      { value: "09", label: "September" },
      { value: "10", label: "October" },
      { value: "11", label: "November" },
      { value: "12", label: "December" },
    ];
  };

  const generateYears = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = currentYear - 5; i <= currentYear + 1; i++) {
      years.push({ value: i.toString(), label: i.toString() });
    }
    return years;
  };

  const generateReport = async () => {
    setLoading(true);
    setError("");

    try {
      const token = localStorage.getItem("token");
      const requestData = {
        reportType: selectedReport,
        startDate: localToUtcIso(startDate, startTime),
        endDate: localToUtcIso(endDate, endTime, true),
        clientId: selectedClient === "all" ? null : parseInt(selectedClient),
        operatorId:
          selectedOperator === "all" ? null : parseInt(selectedOperator),
        format: "json",
        page: 1,
        pageSize: 50,
      };

      const response = await fetch(`/api/reports/${selectedReport}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(requestData),
      });

      if (response.ok) {
        const data = await response.json();
        setReportData(data.data);
      } else {
        const errorData = await response.json();
        setError(errorData.message || "Failed to generate report");
      }
    } catch (error) {
      setError("Error generating report: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const downloadReport = async () => {
    try {
      const token = localStorage.getItem("token");
      const requestData = {
        reportType: selectedReport,
        startDate: localToUtcIso(startDate, startTime),
        endDate: localToUtcIso(endDate, endTime, true),
        clientId: selectedClient === "all" ? null : parseInt(selectedClient),
        operatorId:
          selectedOperator === "all" ? null : parseInt(selectedOperator),
        format: "csv",
      };

      const response = await fetch(`/api/reports/download/${selectedReport}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(requestData),
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${selectedReport}_${
          new Date().toISOString().split("T")[0]
        }.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } else {
        setError("Failed to download report");
      }
    } catch (error) {
      setError("Error downloading report: " + error.message);
    }
  };

  // DataGrid columns configuration
  const getColumns = () => {
    if (selectedReport === "outbound-messages-detailed") {
      return [
        { field: "id", headerName: "ID", width: 80 },
        { field: "operatorName", headerName: "Operator", width: 150 },
        { field: "operatorId", headerName: "Operator Id", width: 140 },
        { field: "consumerName", headerName: "Consumer", width: 150 },
        { field: "messageContent", headerName: "Message Content", width: 400 },
        {
          field: "consumerCredits",
          headerName: "Consumer Credit",
          width: 150,
          renderCell: (params) => {
            const value = params.row.consumerCredits;
            return new Intl.NumberFormat("en-US").format(value);
          },
        },
        {
          field: "sentAt",
          headerName: "Sent Date",
          width: 200,
          renderCell: (params) => {
            const date = params.row.sentAt;
            return date ? formatDateTime(date) : "N/A";
          },
        },
      ];
    }

    if (selectedReport === "inbound-messages") {
      return [
        { field: "id", headerName: "ID", width: 80 },
        {
          field: "receivedDate",
          headerName: "Date Time",
          width: 260,
          renderCell: (params) => {
            const date = params.row.receivedDate;
            return date ? formatDateTime(date) : "N/A";
          },
        },
        { field: "consumerName", headerName: "Consumer", width: 150 },
        { field: "messageContent", headerName: "Message Content", width: 500 },
      ];
    }

    if (selectedReport === "credits-purchased-basic") {
      return [
        { field: "id", headerName: "ID", width: 80 },
        { field: "bundleName", headerName: "Bundle Name", width: 200 },
        { field: "creditsTotal", headerName: "Credits Total", width: 150 },
        { field: "amountTotal", headerName: "Amount Total", width: 150 },
        { field: "dateRange", headerName: "Date Range", width: 320 },
      ];
    }

    if (selectedReport === "credits-purchased-detailed") {
      return [
        { field: "id", headerName: "ID", width: 80 },
        { field: "clientName", headerName: "Client", width: 150 },
        { field: "bundleName", headerName: "Bundle Name", width: 160 },
        { field: "transactionId", headerName: "Transaction ID", width: 330 },
        { field: "creditPurchase", headerName: "Credit Purchase", width: 120 },
        { field: "amountPurchase", headerName: "Amount", width: 120 },
        {
          field: "receivedDate",
          headerName: "Date Time",
          width: 200,
          renderCell: (params) => {
            const date = params.row.receivedDate;
            return date ? formatDateTime(date) : "N/A";
          },
        },
      ];
    }

    if (selectedReport === "operator-pay-basic") {
      return [
        { field: "id", headerName: "ID", width: 80 },
        { field: "operatorName", headerName: "Operator", width: 150 },
        {
          field: "operatorEmail",
          headerName: "Operator Email",
          width: 260,
          cellClassName: "email-cell",
        },
        { field: "operatorId", headerName: "Operator Id", width: 140 },
        { field: "sendCount", headerName: "Sent", width: 120 },
        { field: "amountDue", headerName: "Amount Due", width: 120 },
      ];
    }

    if (selectedReport === "operator-pay-detailed") {
      return [
        { field: "id", headerName: "ID", width: 80 },
        { field: "operatorName", headerName: "Operator", width: 150 },
        { field: "operatorId", headerName: "Operator Id", width: 140 },
        { field: "consumerName", headerName: "Consumer", width: 150 },
        { field: "clientName", headerName: "Client", width: 150 },
        { field: "amountDue", headerName: "Amount Due", width: 120 },
      ];
    }

    if (selectedReport === "client-billing") {
      return [
        { field: "id", headerName: "ID", width: 80 },
        { field: "clientName", headerName: "Client", width: 200 },
        { field: "messageCount", headerName: "Messages Sent", width: 150 },
        {
          field: "payoutRate",
          headerName: "Payout Rate",
          width: 150,
          renderCell: (params) => {
            const currency = params.row.currency || "USD";
            const symbol = currency === "GBP" ? "£" : currency === "EUR" ? "€" : "$";
            return `${symbol}${params.row.payoutRate?.toFixed(2) || "0.00"}`;
          },
        },
        {
          field: "totalAmount",
          headerName: "Total",
          width: 150,
          renderCell: (params) => {
            const currency = params.row.currency || "USD";
            const symbol = currency === "GBP" ? "£" : currency === "EUR" ? "€" : "$";
            return `${symbol}${params.row.totalAmount?.toFixed(2) || "0.00"}`;
          },
        },
        { field: "dateRange", headerName: "Date Range", width: 320 },
      ];
    }

    // outbound-messages-basic As it default selected
    return [
      { field: "id", headerName: "ID", width: 80 },
      { field: "operatorName", headerName: "Operator", width: 150 },
      {
        field: "operatorEmail",
        headerName: "Operator Email",
        width: 260,
        cellClassName: "email-cell",
      },
      { field: "operatorId", headerName: "Operator Id", width: 140 },
      { field: "sendCount", headerName: "Sent", width: 120 },
      { field: "dateRange", headerName: "Date Range", width: 320 },
    ];
  };

  // Transform report data for DataGrid
  const getDataGridRows = () => {
    if (!reportData || !reportData.data) {
      return [];
    }

    return reportData.data.map((item, index) => ({
      id: index + 1,
      operatorName: item.operatorName || "N/A",
      operatorEmail: item.email || item.operatorEmail || "N/A",
      operatorId: item.operatorId || "N/A",
      sendCount: item.sendCount || 0,
      dateRange: getDateRangeDisplay(),
      consumerName: item.consumerName || "N/A",
      messageContent: item.messageContent || "N/A",
      receivedDate: item.receivedDate || "N/A",
      consumerCredits: item.consumerCredits || 0,
      sentAt: item.sentAt || null,
      bundleName: item.bundleName || "N/A",
      transactionId: item.transactionId || "N/A",
      creditsTotal: item.creditsTotal || 0,
      amountTotal: item.amountTotal || 0,
      creditPurchase: item.creditPurchase || 0,
      amountPurchase: item.amountPurchase || 0,
      clientName: item.clientName || "N/A",
      amountDue: item.amountDue || 0,
      // Client billing report fields
      messageCount: item.messageCount || 0,
      payoutRate: item.payoutRate || 0,
      totalAmount: item.totalAmount || 0,
      currency: item.currency || "USD",
      amountDue: `£${item.amountDue || 0}`,
    }));
  };

  // menu props for fixed height
  const menuPropsForSelect = {
    MenuProps: {
      MenuListProps: {
        sx: {
          maxHeight: 250, // Set your desired max height here
          overflowY: "auto", // Enable vertical scrolling
        },
      },
    },
  };

  return (
    <div style={{ padding: "20px", maxWidth: "1200px", margin: "0 auto" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 30,
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <h1 className={styles.dashboardTitle}>REPORTS</h1>
      </div>

      <Card sx={{ mb: 1, boxShadow: "none" }}>
        <CardContent sx={{ p: 0 }}>
          {/* Row 1: Client, Date, Month, Year, Calendar Icon, Time fields */}
          <Grid container spacing={2} sx={{ mb: 3 }} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                select
                fullWidth
                label="Client"
                value={selectedClient}
                onChange={(e) => (
                  setSelectedClient(e.target.value), setSelectedOperator("all")
                )}
                disabled={loading}
                sx={{ minWidth: 280 }}
                inputProps={{
                  "aria-label": "Select client",
                }}
                SelectProps={menuPropsForSelect}
              >
                <MenuItem value="all">All</MenuItem>
                {clients.map((client) => (
                  <MenuItem key={client.id} value={client.id.toString()}>
                    {client.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={6} md={1}>
              <TextField
                type="date"
                label="Start Date"
                name="startDate"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                size="small"
                inputProps={{
                  max: new Date().toISOString().split("T")[0],
                }}
                InputLabelProps={{ shrink: true }}
                disabled={loading}
                sx={{ minWidth: 180 }}
              />
            </Grid>

           
            <Grid item xs={9} md={3}>
              <TextField
                type="time"
                fullWidth
                label="Start Time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                InputLabelProps={{ shrink: true }}
                disabled={loading}
                size="small"
                inputProps={{
                  step: 300,
                  "aria-label": "Select start time",
                }}
                sx={{ minWidth: 108 }}
              />
            </Grid>
          </Grid>

          {/* Row 2: Report Type, End Date, Month, Year, Calendar Icon, Time */}
          <Grid container spacing={2} sx={{ mb: 3 }} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                select
                fullWidth
                label="Report Type"
                value={selectedReport}
                onChange={(e) => setSelectedReport(e.target.value)}
                disabled={loading}
                sx={{ minWidth: 280 }}
                inputProps={{
                  "aria-label": "Select report type",
                }}
              >
                {reportTypes.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={6} md={1}>
              <TextField
                type="date"
                label="End Date"
                name="endDate"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                size="small"
                inputProps={{
                  max: new Date().toISOString().split("T")[0],
                }}
                InputLabelProps={{ shrink: true }}
                disabled={loading}
                sx={{ minWidth: 180 }}
              />
            </Grid>

           

            <Grid item xs={9} md={3}>
              <TextField
                type="time"
                fullWidth
                label="End Time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                InputLabelProps={{ shrink: true }}
                disabled={loading}
                size="small"
                inputProps={{
                  step: 300,
                  "aria-label": "Select end time",
                }}
                sx={{ minWidth: 108 }}
              />
            </Grid>
          </Grid>

          {/* Row 3: Operator dropdown and Action Buttons */}
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Operator"
                value={selectedOperator}
                onChange={(e) => setSelectedOperator(e.target.value)}
                disabled={
                  loading ||
                  selectedReport === "inbound-messages" ||
                  selectedReport === "credits-purchased-basic" ||
                  selectedReport === "credits-purchased-detailed" ||
                  selectedReport === "client-billing"
                }
                sx={{ minWidth: 280 }}
                placeholder={
                  selectedClient === "all" ? "All operators" : "Select operator"
                }
                inputProps={{
                  "aria-label": "Select operator",
                }}
                SelectProps={menuPropsForSelect}
              >
                <MenuItem value="all">
                  {selectedClient === "all" ? "All operators" : "All"}
                </MenuItem>
                {operators.map((operator) => (
                  <MenuItem key={operator.id} value={operator.id.toString()}>
                    {operator.name ||
                      `${operator.firstName} ${operator.lastName}`}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
                <Button
                  onClick={downloadReport}
                  disabled={!reportData || loading}
                  startIcon={
                    <CloudDownloadIcon sx={{ color: "var(--color-white)" }} />
                  }
                  aria-label="Download CSV report"
                  sx={{ padding: "8px 16px" }}
                >
                  Download Report
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} role="alert">
          {error}
        </Alert>
      )}

      {reportData && (
        <Paper
          sx={{ height: 470, width: "100%", borderRadius: 2, boxShadow: 2 }}
        >
          <DataGrid
            rows={getDataGridRows()}
            columns={getColumns()}
            rowHeight={36}
            loading={loading}
            initialState={{
              columns: {
                columnVisibilityModel: {
                  id: false, // 👈 hides the 'id' column
                },
              },
              pagination: { paginationModel },
            }}
            pageSizeOptions={[10, 20, 50]}
            disableRowSelectionOnClick
            sx={{
              border: 0,
              "& .MuiDataGrid-columnHeaderTitle": {
                fontSize: "14px",
                fontWeight: "bold",
              },
            }}
          />
        </Paper>
      )}
    </div>
  );
}
